import React, { useEffect, useState } from "react";
import {
  Alert,
  Form,
  Modal,
  Select,
  Input,
  FormInstance,
  Radio,
  Spin,
} from "antd";
import { FrontDeskBooking } from "../../../types";
import { frontDeskService } from "../../../services/adminService";
import { PAYMENT_METHOD_OPTIONS } from "../../../constants/paymentMethods";

interface CheckOutModalProps {
  open: boolean;
  onCancel: () => void;
  form: FormInstance;
  onSubmit: (values: any) => void;
  selectedCheckOutBooking: FrontDeskBooking | null;
  loading?: boolean;
}

const fmt = (v: number) =>
  `LKR ${Number(v).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

export const CheckOutModal: React.FC<CheckOutModalProps> = ({
  open,
  onCancel,
  form,
  onSubmit,
  selectedCheckOutBooking,
  loading,
}) => {
  const [breakdown, setBreakdown] = useState<any>(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [checkoutMode, setCheckoutMode] = useState<"settle" | "outstanding">(
    "settle",
  );

  const bookingId = selectedCheckOutBooking?.id;

  useEffect(() => {
    if (!open || !bookingId) {
      setBreakdown(null);
      return;
    }
    let cancelled = false;
    setBreakdown(null);
    setCheckoutMode("settle");
    setBreakdownLoading(true);
    // A group is one account: the balance shown (and settled) here is the
    // whole group balance, so anything already collected is never taken again.
    const breakdownId = selectedCheckOutBooking?.group_id
      ? `group-${selectedCheckOutBooking.group_id}`
      : bookingId;
    frontDeskService
      .getInvoiceBreakdown(breakdownId)
      .then((res: any) => {
        if (!cancelled) setBreakdown(res?.data || null);
      })
      .catch(() => {
        if (!cancelled) setBreakdown(null);
      })
      .finally(() => {
        if (!cancelled) setBreakdownLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, bookingId, selectedCheckOutBooking?.group_id]);

  const balance = breakdown ? Number(breakdown.balanceDue) : 0;
  const hasBalance = !!breakdown && balance > 0.009;
  const isGroupStay = breakdown?.is_group === true;

  const handleSubmit = (values: any) => {
    const outstanding = hasBalance && checkoutMode === "outstanding";
    onSubmit({
      payment_method: outstanding ? "outstanding" : values.payment_method,
      transaction_id: outstanding ? undefined : values.transaction_id || undefined,
      settle_balance: hasBalance && checkoutMode === "settle",
      allow_outstanding_balance: outstanding,
      outstanding_reason: outstanding ? values.outstanding_reason : undefined,
    });
  };

  return (
    <Modal
      open={open}
      title={`Check-Out${selectedCheckOutBooking ? ` - ${selectedCheckOutBooking.guest_name}` : ""}`}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={loading}
      okText={hasBalance && checkoutMode === "outstanding" ? "Check Out (Outstanding)" : "Check Out"}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ payment_method: "cash" }}
      >
        {breakdownLoading ? (
          <div style={{ marginBottom: 16 }}>
            <Spin size="small" /> Loading folio balance...
          </div>
        ) : breakdown ? (
          <Alert
            type={hasBalance ? "warning" : "success"}
            showIcon
            style={{ marginBottom: 16 }}
            message={
              hasBalance
                ? isGroupStay
                  ? "Outstanding group balance"
                  : "Outstanding folio balance"
                : "No outstanding balance"
            }
            description={
              <div>
                <div>
                  Total charges: <strong>{fmt(breakdown.grossTotal)}</strong>
                </div>
                <div>
                  Payments received: <strong>{fmt(breakdown.paidToDate)}</strong>
                </div>
                <div>
                  Balance due:{" "}
                  <strong style={{ color: hasBalance ? "#cf1322" : "#389e0d" }}>
                    {fmt(balance)}
                  </strong>
                </div>
                {isGroupStay && (
                  <div style={{ marginTop: 4, fontSize: 12 }}>
                    Covers every room of this group — amounts already collected
                    are included.
                  </div>
                )}
              </div>
            }
          />
        ) : null}

        {hasBalance && (
          <Form.Item
            label="Outstanding balance handling"
            name="checkout_mode"
            initialValue="settle"
          >
            <Radio.Group
              optionType="button"
              block
              value={checkoutMode}
              onChange={(e) => setCheckoutMode(e.target.value)}
              options={[
                {
                  label: `Settle balance now (${fmt(balance)})`,
                  value: "settle",
                },
                {
                  label: "Allow outstanding balance",
                  value: "outstanding",
                },
              ]}
            />
          </Form.Item>
        )}

        {(!hasBalance || checkoutMode === "settle") && (
          <>
            <Form.Item
              name="payment_method"
              label="Payment Method"
              rules={[{ required: true, message: "Select a payment method" }]}
            >
              <Select options={PAYMENT_METHOD_OPTIONS} />
            </Form.Item>

            <Form.Item
              name="transaction_id"
              label="Transaction / Reference No (Optional)"
            >
              <Input placeholder="Enter payment reference" />
            </Form.Item>
          </>
        )}

        {hasBalance && checkoutMode === "outstanding" && (
          <Form.Item
            name="outstanding_reason"
            label="Reason for leaving an outstanding balance"
            rules={[
              {
                required: true,
                min: 5,
                message: "Enter a reason (at least 5 characters)",
              },
            ]}
          >
            <Input.TextArea
              rows={2}
              placeholder="e.g. Guest will pay by bank transfer next week"
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};
